<script setup lang="ts">
import { asText, isFilled } from '@prismicio/client'
import type { Content } from "@prismicio/client";
import { getSliceComponentProps } from '@prismicio/vue'
import { colorizeTags } from '~/utils/colorizeTags'
import { seedWizardDetails } from '~/composables/useOfferteWizard'

const props = defineProps(getSliceComponentProps<Content.OffertaHeroFormSlice>());

const router = useRouter()

const interestsOptions = computed(() => {
  const raw = props.slice.primary.interests_options || ''
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
})

const ratingValue = computed(() => {
  const r = props.slice.primary.rating
  return typeof r === 'number' ? r.toString().replace('.', ',') : ''
})

const headingHtml = computed(() => colorizeTags(asText(props.slice.primary.heading)))

const form = reactive({
  first_name: '',
  last_name: '',
  phone: '',
  postcode: '',
  house_number: '',
  street: '',
  city: '',
  email: '',
  interest: '',
  honeypot: '',
})

const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submitError = ref('')

type LookupStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error'

const lookupStatus = ref<LookupStatus>('idle')
let lookupTimer: ReturnType<typeof setTimeout> | null = null
let lookupSeq = 0

function isLikelyPostcode(s: string): boolean {
  return /^\s*[1-9][0-9]{3}\s*[A-Za-z]{2}\s*$/.test(s)
}

function splitHouseNumber(value: string): { number: string; addition: string } {
  const match = value.trim().match(/^(\d+)\s*(.*)$/)
  if (!match) return { number: '', addition: '' }
  return { number: match[1] || '', addition: (match[2] || '').trim() }
}

function scheduleLookup() {
  if (lookupTimer) clearTimeout(lookupTimer)
  const { number } = splitHouseNumber(form.house_number)
  if (!isLikelyPostcode(form.postcode) || !number) {
    lookupStatus.value = 'idle'
    return
  }
  lookupTimer = setTimeout(runLookup, 400)
}

async function runLookup() {
  const seq = ++lookupSeq
  const { number, addition } = splitHouseNumber(form.house_number)
  lookupStatus.value = 'loading'
  try {
    const res = await $fetch<{
      ok: boolean
      error?: string
      address?: { street: string; city: string }
    }>('/api/postcode-lookup', {
      query: {
        postcode: form.postcode,
        house_number: number,
        house_addition: addition,
      },
    })
    if (seq !== lookupSeq) return
    if (res.ok && res.address) {
      form.street = res.address.street || ''
      form.city = res.address.city || form.city
      lookupStatus.value = 'found'
    } else if (res.error === 'not_found') {
      form.street = ''
      lookupStatus.value = 'not_found'
    } else if (res.error === 'not_configured') {
      lookupStatus.value = 'idle'
    } else {
      lookupStatus.value = 'error'
    }
  } catch {
    if (seq !== lookupSeq) return
    lookupStatus.value = 'error'
  }
}

watch(() => [form.postcode, form.house_number], scheduleLookup)

onBeforeUnmount(() => {
  if (lookupTimer) clearTimeout(lookupTimer)
})

const lookupLine = computed(() => {
  if (lookupStatus.value === 'loading') return 'Adres ophalen…'
  if (lookupStatus.value === 'found' && form.street) {
    return `${form.street} ${form.house_number}, ${form.city}`
  }
  if (lookupStatus.value === 'not_found') return 'Geen adres gevonden — controleer postcode en huisnummer.'
  if (lookupStatus.value === 'error') return 'Adres ophalen mislukt — probeer het opnieuw.'
  return ''
})

const wizardHandoffEnabled = computed(() => {
  // Boolean field; treat null/undefined as the schema default of `true`
  // so existing slice instances keep their current behaviour.
  const v = props.slice.primary.enable_wizard_handoff
  return v === undefined || v === null ? true : !!v
})

const isReadyForWizard = computed(() =>
  wizardHandoffEnabled.value &&
  !!form.first_name.trim() &&
  !!form.last_name.trim() &&
  !!form.phone.trim() &&
  !!form.postcode.trim() &&
  !!form.house_number.trim() &&
  !!form.email.trim() &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()),
)

const submitLabel = computed(() => props.slice.primary.submit_button_text || 'Verstuur aanvraag')

function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  const w = window as unknown as { gtag?: (...args: unknown[]) => void }
  if (typeof w.gtag === 'function') {
    w.gtag('event', name, { flow: 'offerta_hero_form', ...params })
  }
}

async function submitContact() {
  submitError.value = ''
  isSubmitting.value = true
  trackEvent('quote_request_submit_attempt', {
    source: 'hero_form',
    interest: form.interest,
  })
  try {
    const { number: houseNumber, addition: houseAddition } = splitHouseNumber(form.house_number)
    const addressLine = [form.street, form.house_number].filter((p) => p && p.trim()).join(' ').trim()
    const res = await $fetch<{ ok: boolean; error?: string }>('/api/quote-request', {
      method: 'POST',
      body: {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        postal_code: form.postcode,
        address: addressLine,
        street: form.street,
        house_number: houseNumber,
        house_addition: houseAddition,
        city: form.city,
        subject: form.interest || 'Offerte aanvraag via hero formulier',
        message: '',
        honeypot: form.honeypot,
        terms: true,
      },
    })
    if (res.ok) {
      isSubmitted.value = true
      trackEvent('generate_lead_quote', { source: 'hero_form', currency: 'EUR' })
    } else {
      submitError.value = res.error === 'validation'
        ? 'Niet alle verplichte velden zijn ingevuld.'
        : res.error === 'rate_limited'
          ? 'Je hebt recent al meerdere aanvragen gedaan. Probeer het later opnieuw.'
          : 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
      trackEvent('quote_request_submit_error', { reason: res.error || 'unknown' })
    }
  } catch {
    submitError.value = 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
    trackEvent('quote_request_submit_error', { reason: 'request_failed' })
  } finally {
    isSubmitting.value = false
  }
}

function startWizard() {
  if (form.honeypot.trim()) return // silently drop bots that fill the trap
  const { number: houseNumber, addition: houseAddition } = splitHouseNumber(form.house_number)
  seedWizardDetails({
    first_name: form.first_name,
    last_name: form.last_name,
    email: form.email,
    phone: form.phone,
    postcode: form.postcode,
    house_number: houseNumber,
    house_addition: houseAddition,
    street: form.street,
    city: form.city,
  })
  trackEvent('wizard_handoff', {
    source: 'hero_form',
    interest: form.interest,
  })
  router.push('/offerte-wizard')
}
</script>

<template>
  <section
      :data-slice-type="slice.slice_type"
      :data-slice-variation="slice.variation"
      class="pa-offer-hero"
  >
    <div class="pa-container">
      <div class="pa-offer-hero__inner">
        <!-- Left column -->
        <div class="pa-offer-hero__content">
          <p v-if="slice.primary.tag" class="pa-offer-hero__tag">
            {{ slice.primary.tag }}
          </p>

          <h1 v-if="headingHtml" class="pa-offer-hero__heading" v-html="headingHtml" />

          <div v-if="isFilled.richText(slice.primary.description)" class="pa-offer-hero__description">
            <PrismicRichText :field="slice.primary.description" />
          </div>

          <ul v-if="slice.primary.features?.length" class="pa-offer-hero__features">
            <li
                v-for="(item, index) in slice.primary.features"
                :key="index"
                class="pa-offer-hero__feature"
            >
              <span class="pa-offer-hero__feature-check" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-8 8a1 1 0 01-1.42 0l-4-4a1 1 0 011.42-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </span>
              <span>{{ item.feature_text }}</span>
            </li>
          </ul>

          <div v-if="slice.primary.phone_number" class="pa-offer-hero__phone">
            <span class="pa-offer-hero__phone-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </span>
            <div class="pa-offer-hero__phone-text">
              <p v-if="slice.primary.phone_section_label" class="pa-offer-hero__phone-label">
                {{ slice.primary.phone_section_label }}
              </p>
              <p class="pa-offer-hero__phone-number">
                <a :href="`tel:${slice.primary.phone_number}`">{{ slice.primary.phone_number }}</a>
                <span v-if="slice.primary.phone_hours"> · {{ slice.primary.phone_hours }}</span>
              </p>
            </div>
          </div>

          <div v-if="ratingValue || slice.primary.review_count" class="pa-offer-hero__rating">
            <span class="pa-offer-hero__rating-stars" aria-hidden="true">
              <svg v-for="i in 5" :key="i" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.447a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.539 1.118L10 14.347l-3.37 2.447c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.644 8.154c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
              </svg>
            </span>
            <p class="pa-offer-hero__rating-text">
              <span v-if="ratingValue">{{ ratingValue }}/5</span>
              <span v-if="slice.primary.review_count"> uit {{ slice.primary.review_count }}+ beoordelingen</span>
            </p>
          </div>
        </div>

        <!-- Right column: form card -->
        <div class="pa-offer-hero__card">
          <div class="pa-offer-hero__card-header">
            <h2 v-if="slice.primary.form_title" class="pa-offer-hero__form-title">
              {{ slice.primary.form_title }}
            </h2>
            <div v-if="isFilled.richText(slice.primary.form_description)" class="pa-offer-hero__form-description">
              <PrismicRichText :field="slice.primary.form_description" />
            </div>
          </div>

          <div v-if="isSubmitted" class="pa-offer-hero__form-success">
            <h3>Bedankt voor je aanvraag!</h3>
            <p>We hebben je gegevens ontvangen en nemen binnen 2 werkdagen contact met je op.</p>
          </div>

          <form v-else class="pa-offer-hero__form" novalidate @submit.prevent="submitContact">
            <!-- Honeypot — must stay empty -->
            <div class="pa-offer-hero__form-honeypot" aria-hidden="true">
              <label>Website (laat leeg)</label>
              <input
                  v-model="form.honeypot"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
              >
            </div>

            <div class="pa-offer-hero__form-row">
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-first-name">Voornaam</label>
                <input
                    id="oh-first-name"
                    v-model="form.first_name"
                    type="text"
                    name="first_name"
                    placeholder="Voornaam"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-last-name">Achternaam</label>
                <input
                    id="oh-last-name"
                    v-model="form.last_name"
                    type="text"
                    name="last_name"
                    placeholder="Achternaam"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
            </div>

            <div class="pa-offer-hero__form-row">
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-postcode">Postcode</label>
                <input
                    id="oh-postcode"
                    v-model="form.postcode"
                    type="text"
                    name="postcode"
                    placeholder="1234 AB"
                    autocomplete="postal-code"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-house-number">Huisnummer</label>
                <input
                    id="oh-house-number"
                    v-model="form.house_number"
                    type="text"
                    name="house_number"
                    placeholder="21"
                    autocomplete="address-line2"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
            </div>

            <input v-model="form.street" type="hidden" name="street" />
            <input v-model="form.city" type="hidden" name="city" />
            <p
                v-if="lookupLine"
                class="pa-offer-hero__form-lookup"
                :class="{
                  'pa-offer-hero__form-lookup--loading': lookupStatus === 'loading',
                  'pa-offer-hero__form-lookup--found': lookupStatus === 'found',
                  'pa-offer-hero__form-lookup--warn': lookupStatus === 'not_found' || lookupStatus === 'error',
                }"
            >
              {{ lookupLine }}
            </p>

            <div class="pa-offer-hero__form-row">
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-phone">Telefoon</label>
                <input
                    id="oh-phone"
                    v-model="form.phone"
                    type="tel"
                    name="phone"
                    placeholder="06 12 34 56 78"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
              <div class="pa-offer-hero__form-group">
                <label class="pa-offer-hero__form-label" for="oh-email">E-mailadres</label>
                <input
                    id="oh-email"
                    v-model="form.email"
                    type="email"
                    name="email"
                    placeholder="naam@email.nl"
                    class="pa-offer-hero__form-input"
                    required
                >
              </div>
            </div>

            <div v-if="interestsOptions.length" class="pa-offer-hero__form-group">
              <label class="pa-offer-hero__form-label" for="oh-interest">Interesse in</label>
              <select
                  id="oh-interest"
                  v-model="form.interest"
                  name="interest"
                  class="pa-offer-hero__form-input"
              >
                <option v-for="opt in interestsOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </div>

            <div class="pa-offer-hero__form-actions">
              <template v-if="isReadyForWizard">
                <button
                    type="button"
                    class="pa-btn pa-btn--secondary pa-offer-hero__form-submit"
                    :disabled="isSubmitting"
                    @click="startWizard"
                >
                  Offerte samenstellen
                  <span aria-hidden="true">&rarr;</span>
                </button>
                <button
                    type="submit"
                    class="pa-btn pa-btn--outline pa-offer-hero__form-secondary"
                    :disabled="isSubmitting"
                >
                  {{ isSubmitting ? 'Verzenden…' : 'Nee, enkel gegevens achterlaten' }}
                </button>
              </template>
              <button
                  v-else
                  type="submit"
                  class="pa-btn pa-btn--secondary pa-offer-hero__form-submit"
                  :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Verzenden…' : submitLabel }}
                <span v-if="!isSubmitting" aria-hidden="true">&rarr;</span>
              </button>
            </div>

            <p v-if="submitError" class="pa-offer-hero__form-error">{{ submitError }}</p>
          </form>

          <div v-if="isFilled.richText(slice.primary.privacy_notice)" class="pa-offer-hero__form-privacy">
            <span class="pa-offer-hero__form-privacy-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
              </svg>
            </span>
            <PrismicRichText :field="slice.primary.privacy_notice" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
